/* Ported from Concept theme: SliderElement, SliderDots, MediaDots
   Dependencies replaced:
   - Motion.inView → IntersectionObserver
   - theme.utils.debounce → Dawn's debounce()
   - theme.config.rtl → document.documentElement.dir
   - theme.config.motionReduced → prefers-reduced-motion MQL
   - theme.initWhenVisible → IntersectionObserver
*/

(function () {
  var motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function isRTL() { return document.documentElement.dir === 'rtl'; }
  function smoothBehavior() { return motionReduced.matches ? 'auto' : 'smooth'; }

  function localDebounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn.bind(this), wait);
    };
  }

  if (!customElements.get('slider-element')) {
    class SliderElement extends HTMLElement {
      constructor() {
        super();
        var self = this;
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              observer.disconnect();
              self.init();
            }
          });
        }, { rootMargin: '200px 0px 200px 0px' });
        observer.observe(this);
      }

      get looping() { return false; }

      get items() {
        return this._items = this._items || Array.from(
          this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children
        );
      }

      get itemsToShow() {
        return Array.from(this.items).filter(function (el) { return el.clientWidth > 0; });
      }

      get itemOffset() {
        var shown = this.itemsToShow;
        if (shown.length < 2) return 0;
        if (isRTL()) return shown[0].offsetLeft - shown[1].offsetLeft;
        return shown[1].offsetLeft - shown[0].offsetLeft;
      }

      get perPage() {
        var elementWidth = this.clientWidth;
        var mql = window.matchMedia('screen and (min-width: 1280px)');
        if (mql.matches) {
          var styles = window.getComputedStyle(this);
          elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
        }
        return Math.floor(elementWidth / this.itemOffset) || 1;
      }

      get isScrollable() {
        var style = window.getComputedStyle(this);
        var hasOverflow = style.overflow === 'scroll' || style.overflow === 'auto' ||
          style.overflowX === 'scroll' || style.overflowX === 'auto';
        var hasContent = this.scrollWidth > this.clientWidth;
        return hasOverflow && hasContent;
      }

      reset() {
        this._items = Array.from(
          this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children
        );
      }

      init() {
        this.hasPendingOnScroll = false;
        this.currentPage = 1;
        this.updateButtons();
        this.updateTabindex();

        this.addEventListener('scroll', localDebounce(this.update.bind(this), 50));
        this.addEventListener('scrollend', this.scrollend);
        this.addEventListener('slider:previous', this.previous);
        this.addEventListener('slider:next', this.next);

        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          this.addEventListener('shopify:block:select', function (e) {
            e.target.scrollIntoView({ behavior: 'smooth' });
          });
        }
      }

      previous() {
        this.scrollPosition = this.scrollLeft - this.perPage * this.itemOffset * (isRTL() ? -1 : 1);
        this.scrollToPosition(this.scrollPosition);
      }

      next() {
        this.scrollPosition = this.scrollLeft + this.perPage * this.itemOffset * (isRTL() ? -1 : 1);
        this.scrollToPosition(this.scrollPosition);
      }

      select(selectedIndex, immediate) {
        if (typeof immediate === 'undefined') immediate = false;
        this.scrollPosition = this.scrollLeft - (this.currentPage - selectedIndex) * this.itemOffset * (isRTL() ? -1 : 1);
        this.scrollToPosition(this.scrollPosition, immediate);
      }

      selected(selectedIndex) {
        return this.itemsToShow[selectedIndex];
      }

      scrollend() {
        this.hasPendingOnScroll = false;
        this.dispatchEventHandler();
      }

      update() {
        if (window.onscrollend === void 0) {
          clearTimeout(this.scrollendTimeout);
        }

        var previousPage = this.currentPage;
        this.currentPage = Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1;

        if (this.currentPage !== previousPage) {
          if (!this.hasPendingOnScroll) {
            this.dispatchEventHandler();
          }

          this.itemsToShow.forEach(function (item, index) {
            item.classList.toggle('selected', index + 1 === this.currentPage);
          }.bind(this));
        }

        if (window.onscrollend === void 0) {
          var self = this;
          this.scrollendTimeout = setTimeout(function () {
            self.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }));
          }, 75);
        }

        if (this.looping) return;
        this.updateButtons();
      }

      updateButtons() {
        var previousDisabled = this.currentPage === 1;
        var nextDisabled = this.currentPage === this.itemsToShow.length;

        if (this.perPage > 1) {
          previousDisabled = previousDisabled || (this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0);
          nextDisabled = nextDisabled || (this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]));
        }

        this.dispatchEvent(new CustomEvent('slider:previousStatus', {
          bubbles: true,
          detail: { status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible' }
        }));
        this.dispatchEvent(new CustomEvent('slider:nextStatus', {
          bubbles: true,
          detail: { status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible' }
        }));
      }

      updateTabindex() {
        this.removeAttribute('tabindex');
        if (this.isScrollable) this.setAttribute('tabindex', '0');
      }

      isVisible(element, offset) {
        if (typeof offset === 'undefined') offset = 0;
        var lastVisible = this.clientWidth + this.scrollLeft - offset;
        return element.offsetLeft + element.clientWidth <= lastVisible && element.offsetLeft >= this.scrollLeft;
      }

      scrollToPosition(position, immediate) {
        if (typeof immediate === 'undefined') immediate = false;
        this.hasPendingOnScroll = !immediate;
        this.scrollTo({ left: position, behavior: immediate ? 'instant' : smoothBehavior() });
      }

      dispatchEventHandler() {
        this.dispatchEvent(new CustomEvent('slider:change', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.itemsToShow[this.currentPage - 1]
          }
        }));
      }
    }
    customElements.define('slider-element', SliderElement);
  }

  if (!customElements.get('slider-dots')) {
    class SliderDots extends HTMLElement {
      constructor() {
        super();
        var self = this;
        var observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              observer.disconnect();
              self.init();
            }
          });
        });
        observer.observe(this);
      }

      get controlledElement() {
        return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
      }

      get items() {
        return this._items = this._items || Array.from(this.children);
      }

      get itemsToShow() {
        return Array.from(this.items).filter(function (el) { return el.clientWidth > 0; });
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

        if (this.controlledElement) {
          this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));
          this.items.forEach(function (item) {
            item.addEventListener('click', this.onButtonClick.bind(this));
          }.bind(this));
        }
      }

      reset() {
        this._items = Array.from(this.children);
      }

      onChange(event) {
        this.transitionTo(parseInt(event.detail.currentPage) - 1);

        this.itemsToShow.forEach(function (item) {
          item.setAttribute('aria-current',
            parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false'
          );
        });
      }

      transitionTo(selectedIndex, immediate) {
        if (typeof immediate === 'undefined') immediate = false;
        var target = this.itemsToShow[selectedIndex];
        if (target) {
          var scrollEl = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
          if (scrollEl) {
            scrollEl.scrollTo({
              left: target.offsetLeft - scrollEl.clientWidth / 2 + target.clientWidth / 2,
              top: target.offsetTop - scrollEl.clientHeight / 2 - target.clientHeight / 2,
              behavior: immediate ? 'instant' : smoothBehavior()
            });
          }
        }
      }

      onButtonClick(event) {
        event.preventDefault();
        var target = event.currentTarget;

        this.itemsToShow.forEach(function (item) {
          item.setAttribute('aria-current',
            item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false'
          );
        });

        this.controlledElement.select(parseInt(target.getAttribute('data-index')));
      }
    }
    customElements.define('slider-dots', SliderDots);
  }

  if (!customElements.get('media-dots')) {
    class MediaDots extends (customElements.get('slider-dots') || HTMLElement) {
      constructor() {
        super();
      }

      init() {
        super.init();
        this.resetIndexes();
      }

      resetIndexes() {
        var newIndex = 1;
        this.itemsToShow.forEach(function (item, index) {
          item.setAttribute('data-index', newIndex);
          item.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          newIndex++;
        });
      }
    }
    customElements.define('media-dots', MediaDots);
  }
})();
